import { BackgroundAttributes } from '@redfox-thank-you/control-groups';
import { createAttributeManager, StyleRenderer } from '@redfox-thank-you/utils';
import { RichText, useBlockProps } from '@wordpress/block-editor';
import { Notice } from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { store as editorStore } from '@wordpress/editor';
import { __ } from '@wordpress/i18n';
import React from 'react';

import { Settings } from './settings';
import { generateOrderDetailsCSS } from './style';
import type { OrderDetailsEditProps, OrderItem } from './types';

/**
 * Order Details block edit component.
 */
export const OrderDetailsEdit: React.FC< OrderDetailsEditProps > = ( {
	attributes,
	setAttributes,
} ) => {
	// Create attribute manager with utility functions
	const { updateAttributes, getAttribute } = createAttributeManager(
		attributes,
		setAttributes,
	);

	// Extract settings properties with defaults using type-safe methods
	const title = getAttribute< string >(
		'settings.title.text',
		__( 'Order Details', 'redfox-thank-you' ),
	);
	const titleLevel = getAttribute< number >( 'settings.title.level', 2 );
	const showTitle = getAttribute< boolean >( 'settings.title.show', true );

	const showProductImages = getAttribute< boolean >(
		'settings.display.productImages',
		true,
	);
	const showSubtotal = getAttribute< boolean >(
		'settings.display.subtotal',
		true,
	);
	const showPaymentMethod = getAttribute< boolean >(
		'settings.display.paymentMethod',
		true,
	);
	const productImageSize = getAttribute< number >(
		'settings.layout.productImageSize',
		50,
	);

	// Check if we're in a thank you page
	const postType = useSelect( ( select ) => {
		const editorSelect = select(
			editorStore,
		) as unknown as import('@wordpress/editor').EditorStore;
		return editorSelect?.getCurrentPostType?.() as string;
	}, [] );

	// Generate unique block class based on attributes
	const generateBlockClass = () => {
		const cleanName = 'redfox-thank-you-order-details';
		const hash = Math.random().toString( 36 ).substring( 2, 10 );
		return `redfox-thank-you-${ cleanName }-${ hash }`;
	};

	const [ blockClass ] = React.useState( () => generateBlockClass() );

	const blockProps = useBlockProps( {
		className: blockClass,
		style: {
			position: 'relative', // Ensure overlay positioning works correctly
		},
	} );

	// Check if any overlay is enabled across responsive breakpoints
	const hasAnyOverlayEnabled = (
		backgroundStyle: BackgroundAttributes,
	): boolean => {
		// Check responsive structure
		const breakpoints = [ 'desktop', 'tablet', 'mobile' ] as const;
		return breakpoints.some( ( breakpoint ) => {
			const breakpointValues = backgroundStyle?.[ breakpoint ];
			return breakpointValues?.backgroundOverlay?.enabled;
		} );
	};

	// Extract background styles for overlay rendering using attribute manager
	const blockBackgroundStyle = getAttribute< BackgroundAttributes >(
		'style.block.background',
	);

	// Check if overlay is enabled
	const overlayEnabled = hasAnyOverlayEnabled( blockBackgroundStyle );

	// Utility to retrieve plugin URL from localized script & ensure a trailing slash
	const getPluginUrl = (): string => {
		// Prefer value passed from PHP via wp_localize_script – `window.redfoxThankYouSettings.pluginUrl`.
		// Fallback to empty string so concatenation still works in non-WP environments (e.g. Storybook).
		const localizedPluginUrl = window.redfoxThankYouSettings?.pluginUrl ?? '';

		// Ensure trailing slash
		return localizedPluginUrl.replace(/\/?$/, '/');
	};

	const pluginUrl = getPluginUrl();

	// Mock order data for preview
	const mockOrderItems: OrderItem[] = [
		{
			product_name: __( 'Sample Product 1', 'redfox-thank-you' ),
			quantity: 2,
			total: '$29.99',
			product_image: `${ pluginUrl }assets/images/placeholder.png`,
		},
		{
			product_name: __( 'Sample Product 2', 'redfox-thank-you' ),
			quantity: 1,
			total: '$19.99',
			product_image: `${ pluginUrl }assets/images/placeholder.png`,
		},
	];

	// Show notice if not in redfoxwp_thank_you post type
	if ( postType !== 'redfoxwp_thank_you' ) {
		return (
			<div { ...blockProps }>
				<Notice status="warning" isDismissible={ false }>
					{ __(
						'The Order Details block can only be used in Thank You pages.',
						'redfox-thank-you',
					) }
				</Notice>
			</div>
		);
	}

	return (
		<>
			<StyleRenderer
				css={ generateOrderDetailsCSS( attributes, blockClass ) }
				blockId={ blockClass }
			/>
			<Settings
				updateAttributes={ updateAttributes }
				getAttribute={ getAttribute }
			/>

			<div { ...blockProps }>
				{ /* Background Overlay - responsive aware */ }
				{ overlayEnabled && (
					<div
						className="background-overlay" // Don't prefix this class with blockClass to ensure it applies globally.
					/>
				) }

				{ showTitle && (
					<RichText
						tagName={ `h${ titleLevel }` }
						className="redfox-thank-you-order-details__title"
						value={ title }
						onChange={ ( value: string ) =>
							updateAttributes( 'settings.title.text', value )
						}
						placeholder={ __(
							'Order Details',
							'redfox-thank-you',
						) }
						allowedFormats={ [ 'core/bold', 'core/italic' ] }
					/>
				) }

				<div className="redfox-thank-you-order-details__content">
					<div className="redfox-thank-you-order-details__header">
						<div className="redfox-thank-you-order-details__header-cell redfox-thank-you-order-details__header-cell--product-name">
							{ __( 'Product', 'redfox-thank-you' ) }
						</div>
						<div className="redfox-thank-you-order-details__header-cell redfox-thank-you-order-details__header-cell--quantity">
							{ __( 'Qty', 'redfox-thank-you' ) }
						</div>
						<div className="redfox-thank-you-order-details__header-cell redfox-thank-you-order-details__header-cell--total">
							{ __( 'Total', 'redfox-thank-you' ) }
						</div>
					</div>
					<div className="redfox-thank-you-order-details__body">
						{ mockOrderItems.map( ( item, index ) => (
							<div
								key={ index }
								className="redfox-thank-you-order-details__row"
							>
								{ showProductImages && (
									<div
										className="redfox-thank-you-order-details__cell redfox-thank-you-order-details__cell--product-image"
										style={ {
											width: `${ productImageSize }px`,
										} }
									>
										<img
											className="redfox-thank-you-order-details__product-image"
											src={ item.product_image }
											alt={ item.product_name }
											style={ {
												width: `${ productImageSize }px`,
												height: `${ productImageSize }px`,
											} }
										/>
									</div>
								) }
								<div className="redfox-thank-you-order-details__cell redfox-thank-you-order-details__cell--product-name">
									{ item.product_name }
								</div>
								<div
									className="redfox-thank-you-order-details__cell redfox-thank-you-order-details__cell--quantity"
									data-label={ __(
										'Qty:',
										'redfox-thank-you',
									) }
								>
									{ item.quantity }
								</div>
								<div
									className="redfox-thank-you-order-details__cell redfox-thank-you-order-details__cell--total"
									data-label={ __(
										'Total:',
										'redfox-thank-you',
									) }
								>
									{ item.total }
								</div>
							</div>
						) ) }
					</div>

					{ showSubtotal && (
						<div className="redfox-thank-you-order-details__summary">
							<div className="redfox-thank-you-order-details__summary-item">
								<span className="redfox-thank-you-order-details__summary-label">
									{ __( 'Subtotal:', 'redfox-thank-you' ) }
								</span>
								<span className="redfox-thank-you-order-details__summary-value">
									$49.98
								</span>
							</div>
							<div className="redfox-thank-you-order-details__summary-item">
								<span className="redfox-thank-you-order-details__summary-label">
									{ __( 'Shipping:', 'redfox-thank-you' ) }
								</span>
								<span className="redfox-thank-you-order-details__summary-value">
									$5.00
								</span>
							</div>
							<div className="redfox-thank-you-order-details__summary-item">
								<span className="redfox-thank-you-order-details__summary-label">
									{ __( 'Tax:', 'redfox-thank-you' ) }
								</span>
								<span className="redfox-thank-you-order-details__summary-value">
									$4.50
								</span>
							</div>
							<div className="redfox-thank-you-order-details__summary-item redfox-thank-you-order-details__summary-item--total">
								<span className="redfox-thank-you-order-details__summary-label">
									{ __( 'Total:', 'redfox-thank-you' ) }
								</span>
								<span className="redfox-thank-you-order-details__summary-value">
									$59.48
								</span>
							</div>
						</div>
					) }

					{ showPaymentMethod && (
						<div className="redfox-thank-you-order-details__payment">
							<strong className="redfox-thank-you-order-details__payment-label">
								{ __( 'Payment Method:', 'redfox-thank-you' ) }
							</strong>
							<span className="redfox-thank-you-order-details__payment-value">
								{ __(
									'Credit Card (****1234)',
									'redfox-thank-you',
								) }
							</span>
						</div>
					) }
				</div>
			</div>
		</>
	);
};
